

import { IOC } from '@/ioc/index';
import { HttpClient } from '@/src/shared/data/network/http-client';
import { getPathnameWithLocale, type AppLocale } from '@/src/shared/utils/i18n';
import { Inversify } from '@/src/shared/utils/Inversify';

import type { IServerEnvVars } from '@/src/shared/domain/interfaces/server-env-vars.types';
import type { CacheHeadersBuilder } from '@/src/shared/domain/models/cache-headers-builder';
import type { IEntityResolverHttpClientFactory } from '@/src/shared/utils/schema-data-loader';
import type { IHttpClient } from '@schema-data-loader/core/resolver';

// eslint-disable-next-line @typescript-eslint/no-namespace
declare module global {
  /** Cache to speed up hot reload while running the development server */
  let cacheMap: Map<string, string>;
}

class EntityResolverHttpClient implements IHttpClient {
  constructor(
    private readonly envVars: IServerEnvVars,
    private readonly language: AppLocale,
    private readonly client: HttpClient,
    private readonly cacheHeadersBuilder: CacheHeadersBuilder | null
  ) { }

  async get<D = unknown>(url: string, params?: Record<string, unknown>): Promise<D> {
    const route = this.getRoute(url);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await this.client.get<any>(route, {
      params,
      headers: this._authHeaders
    });
    this.cacheHeadersBuilder?.addHeaders(response);
    return response.data.data;
  }

  private getRoute = (url: string) => {
    if (url.startsWith('/')) {
      url = url.substring(1);
    }

    const basePath = `/jsonapi/${url}`;

    return this.shouldNotTranslateRoute(url)
      ? basePath
      : getPathnameWithLocale(this.language, basePath);
  };

  private static avoidTranslationPrefixes = ['media/image/', 'file/file/'];

  private shouldNotTranslateRoute(url: string) {
    return EntityResolverHttpClient.avoidTranslationPrefixes.some((prefix) =>
      url.startsWith(prefix)
    );
  };

  private getCached = async (route: string) => {
    global.cacheMap = global.cacheMap || new Map<string, string>();
    if (global.cacheMap.has(route)) {
      return JSON.parse(global.cacheMap.get(route) ?? '');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await this.client.get<any>(route, {
      headers: this._authHeaders
    });
    global.cacheMap.set(route, JSON.stringify(response.data.data));
    return response.data.data;
  };

  private get _authHeaders(): Record<string, string> {
    return {
      'X-API-Key': this.envVars.cmsJsonApiKey
    };
  }
}

@Inversify.injectable('instance')
export default class EntityResolverHttpClient_Factory implements IEntityResolverHttpClientFactory {
  private readonly client: HttpClient;
  private readonly envVars = IOC.get('ServerEnvVars');

  constructor() {
    this.client = new HttpClient(this.envVars.cmsApiUrl);
  }

  create(language: AppLocale, cacheHeadersBuilder: CacheHeadersBuilder | null): IHttpClient {
    return new EntityResolverHttpClient(this.envVars, language, this.client, cacheHeadersBuilder);
  }
}
